import EmailIcon from '@mui/icons-material/Email';
import InfoIcon from '@mui/icons-material/Info';
import LinkIcon from '@mui/icons-material/Link';
import { alpha, Box, Button, Chip, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenderDetails } from '../../contexts/TenderDetailsContext';
import { type Column, StandardTable } from '../common/UI';
import AnalysisActionCard from './ActionCards/AnalysisActionCard';
import PartnerLogoAvatar from './PartnerLogoAvatar';
import WorkflowStep from './WorkflowStep';

const CapabilityAnalysisStep: React.FC = () => {
  const _theme = useTheme();
  const navigate = useNavigate();
  const {
    tender,
    canManageTender,
    openStep,
    setOpenStep,
    loadingStates,
    handleRegenerate,
    handleDownload,
    handleResetTender,
    handleUpdateServiceStatus,
    handleLinkPartner,
    handleContactPartner,
  } = useTenderDetails();

  if (!canManageTender) {
    return null;
  }

  // biome-ignore lint/suspicious/noExplicitAny: legacy
  const columns: Column<any>[] = [
    {
      id: 'partnerName',
      label: 'Partner Name',
      render: (partner) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PartnerLogoAvatar partner={partner} />
          <Typography
            color="primary"
            sx={{
              cursor: 'pointer',
              textDecoration: 'underline',
              fontWeight: 600,
              fontSize: '1.25rem',
            }}
            onClick={() => navigate(`/partner-details/${partner.partnerId}`)}
          >
            {partner.companyName}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'match',
      label: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          Match
          <Tooltip
            title="Indicates how well the partner's capabilities fill the gaps identified in your original Bid/No-Bid analysis. A higher score means they provide the specific skills or experience you are lacking."
            arrow
            placement="top"
            slotProps={{ tooltip: { sx: { fontSize: '0.875rem', p: 1 } } }}
          >
            <InfoIcon color="action" sx={{ fontSize: 16, cursor: 'help' }} />
          </Tooltip>
        </Box>
      ),
      render: (partner) => (
        <Chip
          label={`${partner.matchScore}%`}
          sx={{
            fontWeight: 'bold',
            fontSize: '1.1rem',
            bgcolor:
              partner.matchScore >= 80
                ? _theme.palette.mode === 'dark'
                  ? '#2e7d32'
                  : '#1b5e20'
                : partner.matchScore >= 60
                  ? _theme.palette.mode === 'dark'
                    ? '#ffb300'
                    : '#e65100'
                  : _theme.palette.mode === 'dark'
                    ? '#616161'
                    : '#757575',
            color:
              partner.matchScore >= 80
                ? '#ffffff'
                : partner.matchScore >= 60
                  ? _theme.palette.mode === 'dark'
                    ? '#000000'
                    : '#ffffff'
                  : '#ffffff',
          }}
        />
      ),
    },
    {
      id: 'impact',
      label: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          Impact
          <Tooltip
            title="The projected overall Bid/No-Bid score if you and this partner submit a joint bid. Compare this to your original score to see the potential improvement."
            arrow
            placement="top"
            slotProps={{ tooltip: { sx: { fontSize: '0.875rem', p: 1 } } }}
          >
            <InfoIcon color="action" sx={{ fontSize: 16, cursor: 'help' }} />
          </Tooltip>
        </Box>
      ),
      render: (partner) => (
        <Chip
          label={`${partner.combinedAlignmentScore}%`}
          sx={{
            fontWeight: 'bold',
            fontSize: '1.1rem',
            bgcolor:
              partner.combinedAlignmentScore >= (tender.score || 0)
                ? _theme.palette.mode === 'dark'
                  ? '#1976d2'
                  : '#0d47a1'
                : _theme.palette.mode === 'dark'
                  ? '#616161'
                  : '#757575',
            color: '#ffffff',
          }}
        />
      ),
    },
    {
      id: 'valueAdd',
      label: 'Value Add',
      render: (partner) => <Box sx={{ fontSize: '0.875rem' }}>{partner.valueAdd}</Box>,
    },
    {
      id: 'actions',
      label: 'Actions',
      render: (partner) => {
        const isLinked = !!(
          tender.sharedWith?.includes(partner.partnerId) ||
          tender.memberPermissions?.[partner.partnerId]
        );

        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {(() => {
              if (isLinked || partner.linkStatus === 'approved') {
                return (
                  <Button variant="contained" size="small" startIcon={<LinkIcon />} disabled>
                    Linked
                  </Button>
                );
              }

              if (partner.linkStatus === 'accepted') {
                return (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<LinkIcon />}
                    onClick={() => handleLinkPartner(partner)}
                    color="success"
                  >
                    Approve Partnership
                  </Button>
                );
              }

              if (partner.linkStatus === 'pending') {
                return (
                  <Button variant="outlined" size="small" startIcon={<EmailIcon />} disabled>
                    Pending Reply
                  </Button>
                );
              }

              if (partner.linkStatus === 'rejected') {
                return (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<EmailIcon />}
                    disabled
                  >
                    Declined
                  </Button>
                );
              }

              return (
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  startIcon={<EmailIcon />}
                  onClick={() => handleContactPartner(partner)}
                  sx={{ fontWeight: 'bold', px: 2 }}
                >
                  Contact
                </Button>
              );
            })()}
          </Box>
        );
      },
    },
  ];

  return (
    <WorkflowStep
      stepNumber={3}
      expanded={openStep === 3}
      onChange={(_, isExpanded) => setOpenStep(isExpanded ? 3 : null)}
      title="Capability Analysis"
      description="Generate and review the Bid/No-Bid analysis."
      isComplete={!!tender.analysisFileLink}
    >
      <AnalysisActionCard
        tender={tender}
        loadingStates={loadingStates}
        onRegenerate={handleRegenerate}
        onDownload={handleDownload}
        onReset={() => handleResetTender('alignmentAnalysis')}
        onUpdateStatus={handleUpdateServiceStatus}
      />

      {tender.alignmentAnalysis?.recommendedPartners &&
        tender.alignmentAnalysis.recommendedPartners.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Recommended JV / Subcontractor Partners
            </Typography>
            <Typography variant="body1" color="text.primary" paragraph sx={{ fontSize: '1.1rem' }}>
              Based on the capability gaps identified in the analysis, these partners are
              recommended to strengthen your bid. Review their profiles, and if they look like a
              good fit, you can pre-approve them or initiate contact.
            </Typography>
            <Box
              sx={{
                mb: 4,
                mt: 3,
                p: 2,
                bgcolor: alpha(_theme.palette.primary.main, 0.04),
                borderRadius: 2,
                border: `1px solid ${alpha(_theme.palette.primary.main, 0.1)}`,
              }}
            >
              <Typography
                variant="h6"
                color="primary.main"
                gutterBottom
                sx={{ fontSize: '1.2rem', fontWeight: 600 }}
              >
                Next Steps for Collaboration:
              </Typography>
              <ol
                style={{
                  margin: 0,
                  paddingLeft: '1.5rem',
                  fontFamily: _theme.typography.fontFamily,
                }}
              >
                <li style={{ marginBottom: '8px' }}>
                  <Typography variant="body1">
                    <strong>Review Profile:</strong> Click on a partner's name to view their full
                    capability profile and past experience.
                  </Typography>
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <Typography variant="body1">
                    <strong>Initiate Contact:</strong> Click 'Contact' to send an initial message to
                    discuss the tender opportunity.
                  </Typography>
                </li>
                <li style={{ marginBottom: '8px' }}>
                  <Typography variant="body1">
                    <strong>Approve & Link:</strong> Once they agree to collaborate, click 'Approve'
                    to send a formal partnership request.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    <strong>Collaborate:</strong> After they accept, they will be securely linked to
                    this tender, allowing you to share documents and work together.
                  </Typography>
                </li>
              </ol>
            </Box>
            <StandardTable
              columns={columns}
              data={tender.alignmentAnalysis.recommendedPartners}
              keyExtractor={(partner) => partner.partnerId}
              size="small"
              emptyStateMessage="No recommended partners found."
            />
          </Box>
        )}
    </WorkflowStep>
  );
};

export default CapabilityAnalysisStep;
